import * as Radix from '@radix-ui/react-dropdown-menu';
import { useQuery } from '@tanstack/react-query';
import { getTimeTillEndOfDayMs } from '@toggle/helpers';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TruncateTooltip } from '~/components/truncate-tooltip/TruncateTooltip';
import { INDEX_ID, WATCHLIST_ID } from '~/config/constants';
import { fetchPublicLists } from '~/services/watchlist/watchlist';
import { Watchlist } from '~/stores/use-watchlist/watchlist-types';

import { AssetsConstituents, SelectedAssetItem } from './RunScenario';
import * as S from './RunScenario.styles';
import { getPublicListId } from './utils/utils';

const MAX_NAME_LENGTH = 15;

interface AssetsDropdownProps {
  selectedAssets?: SelectedAssetItem;
  watchlistLists: Watchlist[];
  handleSelectAssets: (args: SelectedAssetItem) => void;
}

const QUERY_PUBLIC_LISTS = 'QUERY_PUBLIC_LISTS';

export const AssetsDropdown = ({
  selectedAssets,
  watchlistLists,
  handleSelectAssets,
}: AssetsDropdownProps) => {
  const { t } = useTranslation(['calendar']);
  const { data: publicLists } = useQuery({
    queryKey: [QUERY_PUBLIC_LISTS],
    queryFn: fetchPublicLists,
    staleTime: getTimeTillEndOfDayMs(),
  });

  const [isOpen, setIsOpen] = useState(false);

  const watchlistOptions = watchlistLists.map(list => ({
    id: list.id,
    label: <TruncateTooltip text={list.name} atLength={MAX_NAME_LENGTH} />,
  }));
  const indexOptions = [
    {
      id:
        getPublicListId(AssetsConstituents.SPX, publicLists)?.id ||
        AssetsConstituents.SPX,
      /* i18next-extract-mark-context-next-line ["spx", "ftse100", "dji", "ni225"] */
      label: t('calendar:scenario.assetsOption', {
        context: AssetsConstituents.SPX,
      }),
    },
    {
      id:
        getPublicListId(AssetsConstituents.FTSE100, publicLists)?.id ||
        AssetsConstituents.FTSE100,
      /* i18next-extract-mark-context-next-line ["spx", "ftse100", "dji", "ni225"] */
      label: t('calendar:scenario.assetsOption', {
        context: AssetsConstituents.FTSE100,
      }),
    },
    {
      id:
        getPublicListId(AssetsConstituents.DJI, publicLists)?.id ||
        AssetsConstituents.DJI,
      /* i18next-extract-mark-context-next-line ["spx", "ftse100", "dji", "ni225"] */
      label: t('calendar:scenario.assetsOption', {
        context: AssetsConstituents.DJI,
      }),
    },
    {
      id:
        getPublicListId(AssetsConstituents.NI225, publicLists)?.id ||
        AssetsConstituents.NI225,
      /* i18next-extract-mark-context-next-line ["spx", "ftse100", "dji", "ni225"] */
      label: t('calendar:scenario.assetsOption', {
        context: AssetsConstituents.NI225,
      }),
    },
  ];
  const options = [...watchlistOptions, ...indexOptions];

  return (
    <S.FilterWrapper>
      <S.FilterLabel>{t('calendar:scenario.whatLabel')}</S.FilterLabel>
      <Radix.Root open={isOpen} onOpenChange={setIsOpen}>
        <S.Trigger $active={!!selectedAssets} onClick={() => setIsOpen(true)}>
          <S.TriggerLabel data-testid="asset-dropdown-label">
            {options.find(option => option.id === selectedAssets?.id)?.label ??
              t('calendar:scenario.addAssets')}
          </S.TriggerLabel>
          <S.TriggerArrow iconName="ChevronLightDown" />
        </S.Trigger>
        <Radix.Portal>
          <S.DropdownMenuContent
            align="start"
            sideOffset={5}
            collisionPadding={5}
            onInteractOutside={() => setIsOpen(false)}
          >
            {watchlistOptions && (
              <>
                <S.WatchlistGroup>
                  {t('calendar:scenario.watchlist')}
                </S.WatchlistGroup>
                <S.StyledRadioGroup
                  items={watchlistOptions}
                  activeId={selectedAssets?.id}
                  onClick={(id: string) =>
                    handleSelectAssets({
                      assetFilterValueKey: WATCHLIST_ID,
                      id,
                    })
                  }
                />
                <S.Separator />
              </>
            )}
            <S.StyledRadioGroup
              items={indexOptions}
              activeId={selectedAssets?.id}
              onClick={(id: string) =>
                handleSelectAssets({
                  assetFilterValueKey: INDEX_ID,
                  id,
                })
              }
            />
          </S.DropdownMenuContent>
        </Radix.Portal>
      </Radix.Root>
    </S.FilterWrapper>
  );
};
